import Seo from "../components/Seo";
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LineChart, Line, Area, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ComposedChart } from "recharts";
import { Sparkles, Play, TrendingUp, AlertTriangle, FlaskConical, Wallet, Dice5, ArrowLeft, ArrowRight, Lock, Eye, Info, BookOpen, LineChart as LineChartIcon } from "lucide-react";
import { useAuth } from "../lib/auth";
import SimpleBacktestPanel from "../components/SimpleBacktestPanel";
import WalletSimulator from "../components/WalletSimulator";
import TickerCombobox from "../components/TickerCombobox";
import AdvancedChartsPanel from "../components/AdvancedChartsPanel";
import ToolPaywall from "../components/ToolPaywall";
import CandlesBackground from "../components/CandlesBackground";
import AdminReaderPreview, { TOOL_VIEWS, readerViewLabel } from "../components/AdminReaderPreview";
import TierPill from "../components/TierBadge";
import { tierStyle } from "../lib/tiers";

// ─────────────────────────────── Tier helpers ───────────────────────────────
// Accès strict par offre : un outil Pro n'est pas inclus dans Essentiel.
const TIER_ORDER = { free: 0, essentiel: 1, pro: 2, max: 3 };
function tierCovers(userTier, required) {
  return (TIER_ORDER[userTier] ?? 0) >= (TIER_ORDER[required] ?? 0);
}
const TIER_LABEL = { free: "Gratuit", essentiel: "Essentiel", pro: "Pro", max: "Max" };

function KPI({ label, value, tone = "default" }) {
  const cls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-rose-400" : "text-zinc-200";
  return (
    <div className="p-3 rounded-lg border border-white/5 bg-white/[0.02]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`mt-1.5 text-xl font-display font-black font-mono-num ${cls}`}>{value}</div>
    </div>
  );
}

function fmtUSD(n) {
  return n == null ? "—" : `$${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}`;
}
function fmtPct(n) {
  const v = Number(n) || 0;
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

// ─────────────────────────────── Monte Carlo ───────────────────────────────
function MonteCarloSim() {
  const [params, setParams] = useState({ symbol: "SPY", initial_amount: 10000, horizon_days: 365, num_simulations: 100000 });
  const [data, setData] = useState(null);
  const mut = useMutation({
    mutationFn: async () => (await api.post("/simulators/monte-carlo", params)).data,
    onSuccess: (r) => setData(r),
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const chartData = data
    ? data.p50.map((_, i) => ({
        day: i,
        p5: data.p5[i], p25: data.p25[i], p50: data.p50[i], p75: data.p75[i], p95: data.p95[i],
      }))
    : [];
  const s = data?.summary;
  // Log-axis range
  const yDomain = data ? (() => {
    const flat = [...data.p5, ...data.p95].filter((v) => v > 0);
    const lo = Math.max(1, Math.min(...flat) * 0.9);
    const hi = Math.max(...flat) * 1.1;
    return [lo, hi];
  })() : [1, 10];

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]" data-testid="sim-monte-carlo">
      <Card className="p-5 bg-card border-white/5 space-y-4">
        <div className="text-xs uppercase tracking-[0.18em] text-emerald-400 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Paramètres</div>
        <div className="grid gap-2">
          <Label>Actif</Label>
          <TickerCombobox
            value={params.symbol}
            onChange={(v) => setParams({ ...params, symbol: v })}
            placeholder="Chercher un actif…"
            testid="mc-ticker-combo"
          />
        </div>
        <div className="grid gap-2">
          <Label>Montant initial</Label>
          <Input
            inputMode="decimal"
            placeholder="ex. 10000"
            value={params.initial_amount || ""}
            onChange={(e) => setParams({ ...params, initial_amount: Number(String(e.target.value).replace(",", ".")) })}
          />
        </div>
        <div className="grid gap-2">
          <Label>Horizon (jours)</Label>
          <Input
            inputMode="numeric"
            placeholder="ex. 365"
            value={params.horizon_days || ""}
            onChange={(e) => setParams({ ...params, horizon_days: Number(e.target.value) })}
          />
        </div>
        <div className="grid gap-2">
          <Label>Nombre de simulations</Label>
          <Input
            inputMode="numeric"
            min={50}
            max={100000}
            placeholder="ex. 100000"
            value={params.num_simulations || ""}
            onChange={(e) => {
              let n = Number(e.target.value);
              if (n > 100000) {
                n = 100000;
                toast.message("Maximum 100 000 simulations");
              }
              setParams({ ...params, num_simulations: n });
            }}
            data-testid="mc-num-sims"
          />
        </div>
        <Button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || !params.symbol}
          className="w-full bg-emerald-500 text-black hover:bg-emerald-300"
          data-testid="mc-run"
        >
          <Play className="w-4 h-4 mr-1.5" /> {mut.isPending ? "Simulation…" : "Lancer la simulation"}
        </Button>
        <p className="text-[11px] text-zinc-500">
          Modèle GBM basé sur les rendements historiques observés (2 ans). 100 000 trajectoires par défaut pour une distribution optimale. Les résultats ne sont pas des prédictions.
        </p>
      </Card>

      <Card className="p-5 bg-card border-white/5">
        {!data ? (
          <div className="h-[420px] grid place-items-center text-zinc-500 text-sm">
            Choisissez un actif et lancez la simulation pour visualiser 100 000 trajectoires (P5 · P25 · médiane · P75 · P95).
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <KPI label="P5 (pire 5%)" value={fmtUSD(s.p5_final)} tone="down" />
              <KPI label="P25" value={fmtUSD(s.p25_final)} />
              <KPI label="Médiane (P50)" value={fmtUSD(s.median_final)} tone="up" />
              <KPI label="P75" value={fmtUSD(s.p75_final)} />
              <KPI label="P95 (meilleur 5%)" value={fmtUSD(s.p95_final)} tone="up" />
            </div>
            <div className="h-[420px]" data-testid="mc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <defs>
                    <linearGradient id="mcOuter" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00BF63" stopOpacity={0.10} />
                      <stop offset="100%" stopColor="#00BF63" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="mcInner" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00BF63" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#00BF63" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#71717a" }} label={{ value: "Jours", position: "insideBottom", offset: -5, fill: "#71717a", fontSize: 10 }} />
                  <YAxis
                    scale="log"
                    domain={yDomain}
                    tick={{ fontSize: 10, fill: "#71717a" }}
                    tickFormatter={(v) => `$${Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}`}
                    allowDataOverflow
                  />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
                    labelStyle={{ color: "#a1a1aa", fontSize: 12 }}
                    formatter={(v, k) => [fmtUSD(v), k.toUpperCase()]}
                  />
                  <Legend
                    verticalAlign="top"
                    wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }}
                  />
                  <ReferenceLine y={params.initial_amount} stroke="#71717a" strokeDasharray="4 4" strokeOpacity={0.5} />
                  {/* Fan bands: P5-P95 outer + P25-P75 inner */}
                  <Area type="monotone" dataKey="p95" stackId="a" stroke="none" fill="url(#mcOuter)" name="P95" />
                  <Area type="monotone" dataKey="p5" stackId="b" stroke="none" fill="#0A0A0A" name="P5" />
                  <Area type="monotone" dataKey="p75" stackId="c" stroke="none" fill="url(#mcInner)" name="P75" />
                  <Area type="monotone" dataKey="p25" stackId="d" stroke="none" fill="#0A0A0A" name="P25" />
                  <Line type="monotone" dataKey="p5"  stroke="#f43f5e" strokeWidth={1} dot={false} name="P5"  />
                  <Line type="monotone" dataKey="p25" stroke="#a78bfa" strokeWidth={1} dot={false} name="P25" />
                  <Line type="monotone" dataKey="p50" stroke="#00BF63" strokeWidth={2.5} dot={false} name="Médiane" />
                  <Line type="monotone" dataKey="p75" stroke="#a78bfa" strokeWidth={1} dot={false} name="P75" />
                  <Line type="monotone" dataKey="p95" stroke="#00BF63" strokeWidth={1} dot={false} name="P95" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-xs text-zinc-500">
              Échelle Y logarithmique · Volatilité annualisée : <span className="text-zinc-300 font-mono-num">{data.annualized_volatility_pct}%</span> · Rendement historique annualisé : <span className="text-zinc-300 font-mono-num">{data.annualized_return_pct}%</span>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────────── DCA (projection) ───────────────────────────────
function DCASim() {
  const [params, setParams] = useState({
    symbol: "SPY",
    contribution_amount: 300,
    frequency: "monthly",
    duration_years: 10,
    end_date: "",
    use_end_date: false,
    expected_return_pct: 8,
    volatility_pct: 18,
    num_simulations: 300,
  });
  const [data, setData] = useState(null);

  const computeYearsFromEndDate = () => {
    if (!params.end_date) return params.duration_years;
    const end = new Date(params.end_date);
    const now = new Date();
    const y = (end - now) / (1000 * 60 * 60 * 24 * 365.25);
    return Math.max(0.1, +y.toFixed(2));
  };

  const mut = useMutation({
    mutationFn: async () => {
      const duration = params.use_end_date ? computeYearsFromEndDate() : params.duration_years;
      return (await api.post("/simulators/dca", {
        symbol: params.symbol,
        contribution_amount: Number(params.contribution_amount) || 0,
        frequency: params.frequency,
        duration_years: Number(duration) || 0,
        expected_return_pct: Number(params.expected_return_pct) || 0,
        volatility_pct: Number(params.volatility_pct) || 0,
        num_simulations: params.num_simulations,
      })).data;
    },
    onSuccess: (r) => setData(r),
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const chartData = data
    ? (data.periods || data.months).map((m, i) => ({ step: m, contrib: data.contributions[i], p10: data.p10[i], p50: data.p50[i], p90: data.p90[i] }))
    : [];
  const s = data?.summary;

  return (
    <div className="grid gap-6 lg:grid-cols-[340px_1fr]" data-testid="sim-dca">
      <Card className="p-5 bg-card border-white/5 space-y-4">
        <div className="text-xs uppercase tracking-[0.18em] text-emerald-400 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Paramètres</div>

        <div className="grid gap-2">
          <Label>Fréquence de versement</Label>
          <Select value={params.frequency} onValueChange={(v) => setParams({ ...params, frequency: v })}>
            <SelectTrigger data-testid="dca-freq"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Quotidienne</SelectItem>
              <SelectItem value="weekly">Hebdomadaire</SelectItem>
              <SelectItem value="monthly">Mensuelle</SelectItem>
              <SelectItem value="annual">Annuelle</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-2">
          <Label>Versement par période ($)</Label>
          <Input
            inputMode="decimal"
            placeholder="ex. 300"
            value={params.contribution_amount || ""}
            onChange={(e) => setParams({ ...params, contribution_amount: e.target.value.replace(",", ".") })}
            data-testid="dca-amount"
          />
        </div>

        <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3 space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="uppercase tracking-[0.15em] text-emerald-400">Durée</span>
            <div className="flex items-center gap-1 text-[10px]">
              <button
                type="button"
                onClick={() => setParams({ ...params, use_end_date: false })}
                className={`px-2 py-1 rounded ${!params.use_end_date ? "bg-emerald-500/20 text-emerald-300" : "text-zinc-500 hover:bg-white/5"}`}
                data-testid="dca-years-mode"
              >Années</button>
              <button
                type="button"
                onClick={() => setParams({ ...params, use_end_date: true })}
                className={`px-2 py-1 rounded ${params.use_end_date ? "bg-emerald-500/20 text-emerald-300" : "text-zinc-500 hover:bg-white/5"}`}
                data-testid="dca-enddate-mode"
              >Date de fin</button>
            </div>
          </div>
          {!params.use_end_date ? (
            <Input
              inputMode="decimal"
              placeholder="ex. 10"
              value={params.duration_years || ""}
              onChange={(e) => setParams({ ...params, duration_years: e.target.value.replace(",", ".") })}
              data-testid="dca-years"
            />
          ) : (
            <Input
              type="date"
              value={params.end_date}
              onChange={(e) => setParams({ ...params, end_date: e.target.value })}
              data-testid="dca-enddate"
            />
          )}
        </div>

        <div className="grid gap-2">
          <Label>Rendement annuel attendu (%)</Label>
          <Input
            inputMode="decimal"
            placeholder="ex. 8"
            value={params.expected_return_pct || ""}
            onChange={(e) => setParams({ ...params, expected_return_pct: e.target.value.replace(",", ".") })}
          />
        </div>
        <div className="grid gap-2">
          <Label>Volatilité annuelle (%)</Label>
          <Input
            inputMode="decimal"
            placeholder="ex. 18"
            value={params.volatility_pct || ""}
            onChange={(e) => setParams({ ...params, volatility_pct: e.target.value.replace(",", ".") })}
          />
        </div>

        <Button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || !params.contribution_amount || (!params.use_end_date ? !params.duration_years : !params.end_date)}
          className="w-full bg-emerald-500 text-black hover:bg-emerald-300"
          data-testid="dca-run"
        >
          <Play className="w-4 h-4 mr-1.5" /> {mut.isPending ? "Calcul…" : "Lancer"}
        </Button>
      </Card>

      <Card className="p-5 bg-card border-white/5">
        {!data ? (
          <div className="h-[420px] grid place-items-center text-zinc-500 text-sm">
            Simulez la performance de votre plan DCA sur N années — versement quotidien, hebdomadaire, mensuel ou annuel.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <KPI label="Total investi" value={fmtUSD(s.total_contributed)} />
              <KPI label="Médiane finale" value={fmtUSD(s.median_final)} tone="up" />
              <KPI label="Multiplicateur médian" value={`${s.median_return_x}×`} tone="up" />
              <KPI label="Pire 10%" value={fmtUSD(s.p10_final)} tone={s.p10_final >= s.total_contributed ? "up" : "down"} />
            </div>
            <div className="h-[380px]" data-testid="dca-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <XAxis dataKey="step" tick={{ fontSize: 10, fill: "#71717a" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#71717a" }} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
                    labelStyle={{ color: "#a1a1aa", fontSize: 12 }}
                    formatter={(v, k) => [fmtUSD(v), k]}
                  />
                  <Line type="monotone" dataKey="p90" stroke="#00BF63" strokeOpacity={0.4} strokeWidth={1} dot={false} name="P90" />
                  <Line type="monotone" dataKey="p50" stroke="#00BF63" strokeWidth={2} dot={false} name="Médiane" />
                  <Line type="monotone" dataKey="p10" stroke="#f43f5e" strokeOpacity={0.6} strokeWidth={1} dot={false} name="P10" />
                  <Line type="monotone" dataKey="contrib" stroke="#71717a" strokeDasharray="4 4" strokeWidth={1.5} dot={false} name="Cumul versements" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────────── Liquidation ───────────────────────────────
function LiquidationSim() {
  const [params, setParams] = useState({ entry_price: 50000, leverage: 10, side: "long", maintenance_margin_pct: 0.5, position_size_usd: 1000 });
  const [data, setData] = useState(null);
  const mut = useMutation({
    mutationFn: async () => (await api.post("/simulators/liquidation", params)).data,
    onSuccess: (r) => setData(r),
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]" data-testid="sim-liquidation">
      <Card className="p-5 bg-card border-white/5 space-y-4">
        <div className="text-xs uppercase tracking-[0.18em] text-emerald-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Paramètres</div>
        <div className="grid gap-2">
          <Label>Prix d'entrée</Label>
          <Input type="number" step="any" value={params.entry_price} onChange={(e) => setParams({ ...params, entry_price: Number(e.target.value) })} data-testid="liq-entry" />
        </div>
        <div className="grid gap-2">
          <Label>Sens</Label>
          <Select value={params.side} onValueChange={(v) => setParams({ ...params, side: v })}>
            <SelectTrigger data-testid="liq-side"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="long">Long</SelectItem>
              <SelectItem value="short">Short</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Levier (x)</Label>
          <Input type="number" step="any" value={params.leverage} onChange={(e) => setParams({ ...params, leverage: Number(e.target.value) })} data-testid="liq-lev" />
        </div>
        <div className="grid gap-2">
          <Label>Marge de maintenance (%)</Label>
          <Input type="number" step="0.1" value={params.maintenance_margin_pct} onChange={(e) => setParams({ ...params, maintenance_margin_pct: Number(e.target.value) })} />
        </div>
        <div className="grid gap-2">
          <Label>Marge (position en $)</Label>
          <Input type="number" value={params.position_size_usd} onChange={(e) => setParams({ ...params, position_size_usd: Number(e.target.value) })} />
        </div>
        <Button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="w-full bg-emerald-500 text-black hover:bg-emerald-300"
          data-testid="liq-run"
        >
          <Play className="w-4 h-4 mr-1.5" /> {mut.isPending ? "Calcul…" : "Calculer"}
        </Button>
      </Card>

      <Card className="p-5 bg-card border-white/5">
        {!data ? (
          <div className="h-[420px] grid place-items-center text-zinc-500 text-sm">
            Calculez votre prix de liquidation et votre courbe P&L.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <KPI label="Prix de liquidation" value={fmtUSD(data.liquidation_price)} tone="down" />
              <KPI label="Mouvement fatal" value={fmtPct(data.liquidation_pct_move)} tone="down" />
              <KPI label="Notionnel" value={fmtUSD(data.notional_usd)} />
              <KPI label="Contrats" value={data.contract_qty} />
            </div>
            <div className="h-[380px]" data-testid="liq-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.curve}>
                  <XAxis dataKey="price_pct_move" tick={{ fontSize: 10, fill: "#71717a" }} unit="%" />
                  <YAxis tick={{ fontSize: 10, fill: "#71717a" }} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
                    labelStyle={{ color: "#a1a1aa", fontSize: 12 }}
                    formatter={(v, k) => [k === "pnl_usd" ? fmtUSD(v) : fmtPct(v), k]}
                  />
                  <ReferenceLine y={0} stroke="#71717a" strokeDasharray="3 3" />
                  <ReferenceLine
                    x={Math.round(data.liquidation_pct_move / 2) * 2}
                    stroke="#f43f5e"
                    strokeDasharray="4 4"
                    label={{ value: "Liquidation", fill: "#f43f5e", fontSize: 10, position: "insideTop" }}
                  />
                  <Line type="monotone" dataKey="pnl_pct_of_margin" stroke="#00BF63" strokeWidth={2} dot={false} name="P&L / marge %" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-3 text-[11px] text-zinc-500">
              Formule standard perp/futures marge isolée. N'inclut pas les frais de funding ni les slippages.
            </p>
          </>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────────── Simulator Catalog ───────────────────────────────
// Catégories de la barre de filtre
const CATEGORIES = [
  { id: "all", label: "Tous les outils", icon: null },
  { id: "analyse", label: "Analyser les marchés", icon: LineChartIcon },
  { id: "test", label: "Tester une stratégie", icon: FlaskConical },
  { id: "risque", label: "Projeter et gérer le risque", icon: Dice5 },
];

const SIM_DISCLAIMER =
  "Simulation à but éducatif, fondée sur des données historiques et des hypothèses. Les performances passées ne préjugent pas des performances futures. Ces résultats ne constituent pas un conseil en investissement.";
const CHART_DISCLAIMER =
  "Graphiques fournis à titre informatif et éducatif. Les performances passées ne préjugent pas des performances futures. Ces informations ne constituent pas un conseil en investissement.";

// Ordre d'affichage : outils gratuits d'abord.
// `course` : cours lié affiché sous l'outil (« Comprendre la méthode »), ex. { title: "Le DCA expliqué", slug: "dca" }.
// Laisser à null tant que le cours n'existe pas.
const SIMULATORS = [
  {
    id: "advanced-charts",
    name: "Graphiques avancés",
    tier: "free",
    category: "analyse",
    icon: LineChartIcon,
    description:
      "Chartisme complet avec indicateurs (RSI, MACD, EMA, Bandes de Bollinger…), timeframes et outils de dessin, propulsé par TradingView.",
    Component: AdvancedChartsPanel,
    disclaimer: CHART_DISCLAIMER,
    course: null,
  },
  {
    id: "dca-projection",
    name: "DCA projection",
    tier: "free",
    category: "risque",
    icon: TrendingUp,
    description:
      "Projetez votre plan de versements réguliers sur plusieurs années avec rendement et volatilité attendus.",
    Component: DCASim,
    disclaimer: SIM_DISCLAIMER,
    course: null,
  },
  {
    id: "backtest-simple",
    name: "Backtest simple",
    tier: "essentiel",
    category: "test",
    icon: FlaskConical,
    description: "Testez une stratégie (Buy & Hold, DCA, croisement SMA) sur un actif et une période historique.",
    Component: SimpleBacktestPanel,
    disclaimer: SIM_DISCLAIMER,
    course: null,
  },
  {
    id: "wallet-dca",
    name: "Wallet DCA multi-actifs",
    tier: "essentiel",
    category: "test",
    icon: Wallet,
    description:
      "Simulez un plan DCA réparti sur plusieurs actifs, avec allocation et règle de prise de bénéfices.",
    Component: WalletSimulator,
    disclaimer: SIM_DISCLAIMER,
    course: null,
  },
  {
    id: "monte-carlo",
    name: "Monte Carlo",
    tier: "pro",
    category: "risque",
    icon: Dice5,
    description: "Projetez 100 000 trajectoires futures d'un actif à partir de sa volatilité historique.",
    Component: MonteCarloSim,
    disclaimer: SIM_DISCLAIMER,
    course: null,
  },
  {
    id: "liquidation-map",
    name: "Liquidation Map",
    tier: "pro",
    category: "risque",
    icon: AlertTriangle,
    description: "Calculez votre prix de liquidation pour une position à levier et visualisez la courbe P&L.",
    Component: LiquidationSim,
    disclaimer: SIM_DISCLAIMER,
    course: null,
  },
];

// Prix des offres, lus depuis /config (ex. « 9,99 €/mois »)
function useTierPrice() {
  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  return (tier) => {
    const entry = cfg?.tiers?.[tier];
    return entry ? `${entry.price_eur.toString().replace(".", ",")} €/mois` : null;
  };
}

// Pastille d'offre aux couleurs du site (Gratuit gris, Essentiel orange, Pro vert, Max violet)
function TierBadge({ sim, allowed }) {
  return (
    <span data-testid={`sim-badge-${sim.id}`}>
      <TierPill
        tier={sim.tier}
        locked={sim.tier !== "free" && !allowed}
        label={sim.tier === "free" ? "Gratuit · sans inscription" : undefined}
      />
    </span>
  );
}

function SimulatorCard({ sim, userTier, price }) {
  const Icon = sim.icon;
  const allowed = tierCovers(userTier, sim.tier);
  const tierLabel = TIER_LABEL[sim.tier] || sim.tier;
  return (
    <Card
      className={`p-6 bg-card border-white/5 flex flex-col gap-4 transition-all ${
        allowed ? "hover:border-orange-500/30" : tierStyle(sim.tier).hover
      }`}
      data-testid={`sim-card-${sim.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-11 h-11 rounded-lg bg-orange-500/10 border border-orange-500/20 grid place-items-center text-orange-300">
          <Icon className="w-5 h-5" />
        </div>
        <TierBadge sim={sim} allowed={allowed} />
      </div>

      <div>
        <h3 className="font-display font-bold text-xl">{sim.name}</h3>
        <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{sim.description}</p>
      </div>

      <div className="mt-auto pt-2">
        {allowed ? (
          <Button asChild className="w-full bg-emerald-500 text-black hover:bg-emerald-300" data-testid={`sim-open-${sim.id}`}>
            <Link to={`/simulateurs/${sim.id}`}>
              <Play className="w-4 h-4 mr-1.5" /> Ouvrir l'outil
            </Link>
          </Button>
        ) : (
          <div className="space-y-2">
            <div className={`flex items-center gap-2 text-xs ${tierStyle(sim.tier).text}`}>
              <Lock className="w-3.5 h-3.5" /> Inclus dès l'offre {tierLabel}
            </div>
            <Button asChild className={`w-full ${tierStyle(sim.tier).cta}`} data-testid={`sim-upgrade-${sim.id}`}>
              <Link to={`/simulateurs/${sim.id}`}>
                Débloquer{price ? ` · ${price}` : ""} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function SimulatorsPage() {
  const { toolId } = useParams();
  const { user } = useAuth();
  const isAdmin = !!(user && user.role === "admin");

  // Aperçu admin : "admin" = vue normale ; sinon simule un visiteur, un membre gratuit, un abonné Essentiel ou Pro.
  const [previewAs, setPreviewAs] = useState("admin");
  const isPreviewing = isAdmin && previewAs !== "admin";
  const userTier = isPreviewing
    ? previewAs === "essentiel" || previewAs === "pro"
      ? previewAs
      : "free"
    : isAdmin
      ? "max"
      : user?.subscription_tier || "free";
  const loggedIn = isPreviewing ? previewAs !== "guest" : !!user;

  const [category, setCategory] = useState("all");
  const priceOf = useTierPrice();

  const active = toolId ? SIMULATORS.find((s) => s.id === toolId) : null;

  // Remonte en haut de page à l'ouverture d'un outil et au retour au catalogue
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [toolId]);

  // Identifiant d'outil inconnu dans l'URL → retour au catalogue
  if (toolId && !active) return <Navigate to="/simulateurs" replace />;

  const canOpen = active && tierCovers(userTier, active.tier);
  const ActiveComponent = canOpen ? active.Component : null;
  const visible = SIMULATORS.filter((s) => category === "all" || s.category === category);

  return (
    <div className="min-h-screen">
      <Seo title="Simulateurs" description="Simulateurs d'investissement Side B : Monte-Carlo, DCA, intérêts composés et plus, pour tester vos stratégies." path="/simulateurs" />
      {isPreviewing && (
        <div className="border-b border-violet-500/20 bg-violet-500/[0.08]" data-testid="admin-preview-banner">
          <div className="mx-auto max-w-7xl px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-violet-200 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Aperçu admin : vous voyez les outils comme
              <b className="font-semibold text-white">{readerViewLabel(previewAs, TOOL_VIEWS)}</b>
            </span>
            <button
              type="button"
              onClick={() => setPreviewAs("admin")}
              className="text-violet-300 hover:text-white underline-offset-4 hover:underline"
            >
              Revenir à ma vue
            </button>
          </div>
        </div>
      )}

      {/* Téléphone : l'en-tête de la page n'est affiché que sur le catalogue (dans un outil, on va droit à l'outil) */}
      <section className={`relative border-b border-white/5 overflow-hidden ${active ? "max-sm:hidden" : ""}`}>
        <div className="absolute inset-0 grid-noise opacity-40" />
        {!active && <CandlesBackground />}
        <div className="relative mx-auto max-w-7xl px-6 py-8 sm:py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-2 sm:mb-3">Outils</div>
          <h1 className="font-display font-black text-[2rem] leading-tight sm:text-5xl">Analysez et testez avant d'investir</h1>
          <p className="mt-2 sm:mt-4 text-[15px] sm:text-base text-zinc-400 max-w-2xl">
            Graphiques, backtests, projections et simulations pour tester vos idées avant de mettre un euro en jeu.
          </p>
        </div>
      </section>

      <section className={`mx-auto max-w-7xl px-6 ${active ? "pt-5" : "pt-6"} pb-8 sm:py-12`}>
        {!active ? (
          <>
            {/* Barre de filtre par catégorie (même style que les onglets de la page Cours).
                Téléphone : une seule ligne qui défile sur le côté */}
            <div
              className="inline-flex flex-wrap items-center gap-1 rounded-lg bg-white/[0.03] border border-white/5 p-1 mb-8 max-sm:flex max-sm:flex-nowrap max-sm:overflow-x-auto scrollbar-none max-sm:-mx-6 max-sm:rounded-none max-sm:border-x-0 max-sm:px-6 max-sm:mb-6"
              data-testid="sim-categories"
            >
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const count = c.id === "all" ? SIMULATORS.length : SIMULATORS.filter((s) => s.category === c.id).length;
                const selected = category === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    data-testid={`sim-category-${c.id}`}
                    className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 rounded-md px-3 sm:px-4 py-2 text-sm transition-colors ${
                      selected ? "bg-emerald-500 text-black font-medium" : "text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    {Icon && <Icon className="w-3.5 h-3.5" />} {c.label}
                    <span className={`text-xs tabular-nums ${selected ? "text-black/60" : "text-zinc-600"}`}>{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="simulators-catalog">
              {visible.map((sim) => (
                <SimulatorCard key={sim.id} sim={sim} userTier={userTier} price={priceOf(sim.tier)} />
              ))}
            </div>
          </>
        ) : (
          <div>
            <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-4 sm:mb-6" aria-label="Fil d'Ariane">
              {/* Téléphone : « ← Outils » sert de bouton retour (le gros bouton est masqué) */}
              <Link to="/simulateurs" className="inline-flex items-center gap-1 hover:text-emerald-400">
                <ArrowLeft className="w-3.5 h-3.5 sm:hidden" /> Outils
              </Link>
              <span>/</span>
              <span className="text-zinc-300">{active.name}</span>
            </nav>

            {/* Téléphone : badge d'offre en haut, puis titre et description en pleine largeur */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 max-sm:flex-col max-sm:items-start max-sm:gap-2 max-sm:mb-5">
              <div className="flex items-center gap-3 min-w-0">
                <Button
                  asChild
                  variant="outline"
                  className="border-white/10 bg-transparent hover:bg-white/5 max-sm:hidden"
                  data-testid="sim-back-btn"
                >
                  <Link to="/simulateurs">
                    <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour aux outils
                  </Link>
                </Button>
                <div className="min-w-0">
                  <h2 className="font-display font-black text-2xl leading-tight">{active.name}</h2>
                  <p className="text-xs text-zinc-500 mt-0.5 max-sm:mt-1 max-sm:leading-relaxed">{active.description}</p>
                </div>
              </div>
              <span className="shrink-0 max-sm:order-first"><TierBadge sim={active} allowed={canOpen} /></span>
            </div>

            {ActiveComponent ? (
              <ActiveComponent />
            ) : (
              <ToolPaywall tier={active.tier} currentTier={userTier} loggedIn={loggedIn} />
            )}

            {active.course && (
              <Link
                to={`/cours/${active.course.slug}`}
                className="mt-6 inline-flex items-center gap-2 text-sm text-orange-300 hover:text-orange-200"
                data-testid="sim-course-link"
              >
                <BookOpen className="w-4 h-4" /> Comprendre la méthode : {active.course.title}
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}

            <div
              className="mt-6 flex gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3 text-xs text-zinc-500 leading-relaxed"
              data-testid="sim-disclaimer"
            >
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <p>{active.disclaimer}</p>
            </div>
          </div>
        )}
      </section>

      {isAdmin && <AdminReaderPreview value={previewAs} onChange={setPreviewAs} views={TOOL_VIEWS} />}
    </div>
  );
}
